/**
 * Exercise prompts per practice word (docs/design/pedagogy-v2.md): the same target word reached
 * in different ways, so practice is not the same drill every turn. Every answer is a floor word
 * with clinician-rated child recordings, so scoring and the simulated users stay verifiable.
 *
 * - `complete`: sentence completion with the picture shown; the sentence ends where the child
 *   says the word (a cloze, as SLPs use between word and sentence level).
 * - `riddle`: a "what am I" clue with the picture hidden until the child answers (word recall
 *   adds load, so the coach uses it only when the child is doing well: the challenge point).
 *
 * Lines are recorded whole in the helper's voice; keep them short (at most 80 characters), with
 * the answer word itself never in the clue.
 */
export interface WordExercises {
  complete: string;
  riddle: string;
}

export const EXERCISES: Readonly<Record<string, WordExercises>> = {
  rock: {
    complete: 'A hard, gray stone is also called a …',
    riddle: 'I’m hard and gray, and you can skip me on a pond. What am I?',
  },
  ring: {
    complete: 'On your finger, you can wear a shiny …',
    riddle: 'I’m round and sparkly, and I sit on your finger. What am I?',
  },
  rope: {
    complete: 'To climb up high, you can hold on to a long …',
    riddle: 'I’m long and twisty, and you can tie me in a knot. What am I?',
  },
  rat: {
    complete: 'A mouse has a bigger cousin called a …',
    riddle: 'I’m like a mouse, but bigger, with a long tail. What am I?',
  },
  rice: {
    complete: 'A bowl full of tiny white grains is a bowl of …',
    riddle: 'I’m tiny white grains, and you can eat me with chopsticks. What am I?',
  },
  rain: {
    complete: 'Drops of water falling from the clouds are called …',
    riddle: 'I fall from the clouds and make puddles. What am I?',
  },
  rocket: {
    complete: 'To fly all the way to the moon, you ride in a …',
    riddle: 'I blast off with fire and fly into space. What am I?',
  },
  robot: {
    complete: 'A metal helper that beeps and moves is a …',
    riddle: 'I’m made of metal, I beep, and I follow instructions. What am I?',
  },
  rabbit: {
    complete: 'An animal with long ears that hops around is a …',
    riddle: 'I have long ears and a fluffy tail, and I love carrots. What am I?',
  },
  rhino: {
    complete: 'A big gray animal with a horn on its nose is a …',
    riddle: 'I’m big and gray, with a horn on my nose. What am I?',
  },
  robin: {
    complete: 'A little bird with an orange chest is a …',
    riddle: 'I’m a small bird with an orange chest, and I sing in spring. What am I?',
  },
  radio: {
    complete: 'To hear music and the news, you can turn on the …',
    riddle: 'You turn me on to hear music and the news. What am I?',
  },
  train: {
    complete: 'Lots of wagons rolling on the tracks make a …',
    riddle: 'I ride on tracks, and I go choo choo. What am I?',
  },
  frog: {
    complete: 'A green animal that hops and says ribbit is a …',
    riddle: 'I’m green, I hop, and I say ribbit. What am I?',
  },
  crab: {
    complete: 'An animal that walks sideways on the beach is a …',
    riddle: 'I walk sideways, and I have two big claws. What am I?',
  },
  crown: {
    complete: 'On her head, the queen wears a golden …',
    riddle: 'A king or a queen wears me on their head. What am I?',
  },
  drum: {
    complete: 'To make a big boom, you hit a …',
    riddle: 'You hit me with sticks, and I go boom. What am I?',
  },
  grapes: {
    complete: 'Little round fruits that grow in bunches are …',
    riddle: 'We’re small and round, and we grow in bunches. What are we?',
  },
  star: {
    complete: 'High in the sky at night, you can see a twinkling …',
    riddle: 'I twinkle in the sky at night. What am I?',
  },
  guitar: {
    complete: 'A music instrument with strings to strum is a …',
    riddle: 'I have strings, and you strum me to play songs. What am I?',
  },
  popcorn: {
    complete: 'At the movies, a crunchy snack that pops is …',
    riddle: 'I pop in the pot, and you eat me at the movies. What am I?',
  },
  acorn: {
    complete: 'A squirrel’s favorite nut from an oak tree is an …',
    riddle: 'I’m a little nut from an oak tree, and squirrels love me. What am I?',
  },
  tornado: {
    complete: 'A wind that spins around and around very fast is a …',
    riddle: 'I’m a wind that spins and spins, very fast. What am I?',
  },
  dinosaur: {
    complete: 'A giant animal that lived long, long ago is a …',
    riddle: 'I lived long, long ago, and some of me were giants. What am I?',
  },
  beard: {
    complete: 'Hair that grows on a man’s chin is a …',
    riddle: 'I’m hair that grows on a chin. What am I?',
  },
  cheer: {
    complete: 'When your team wins, everyone gives a big …',
    riddle: 'When your team scores, everyone shouts me out loud. What am I?',
  },
  square: {
    complete: 'A shape with four equal sides is a …',
    riddle: 'I’m a shape with four equal sides. What am I?',
  },
  fire: {
    complete: 'To keep warm while camping, you can light a …',
    riddle: 'I’m hot and orange, and I crackle at a campsite. What am I?',
  },
  vampire: {
    complete: 'A spooky creature with fangs and a cape is a …',
    riddle: 'I have fangs and a cape, and I come out at night. What am I?',
  },
  chandelier: {
    complete: 'A fancy light with sparkly crystals on the ceiling is a …',
    riddle: 'I hang from the ceiling and sparkle with lights. What am I?',
  },
};
